<script>
  import { onMount, createEventDispatcher } from "svelte";
  import { Stack } from "creditdesign-svelte-components";
  import { SimpleButton } from "nature-immersive-svelte-components";
  import { allowAnimation } from "./stores/prefers-reduced-motion.js";
  import { typewriter } from "./actions/typewriter.js";
  import data from "./content/data.json";

  let {
    headline,
    standfirst,
    prompt,
    definitions,
    source,
    promptHeading,
    outputHeading,
    buttonStartMessage,
    buttonEndMessage,
    buttonNextMessage,
  } = data;

  let definitionsFormatted = definitions
    .map((d) => d.definition)
    .map((d) => d.replace("\\:", ":"));

  definitionsFormatted.unshift(" ");

  let containerElement;
  let mounted = false;
  let definitionIndex = 0;
  let buttonMessage = buttonStartMessage;

  $: currentDefinition = definitionsFormatted[definitionIndex];

  $: letters = Array.from(currentDefinition);

  let promptLine = prompt.split("\n");

  let handleClick = () => {
    if (definitionIndex < definitionsFormatted.length - 1) {
      definitionIndex += 1;
    } else {
      definitionIndex = 0;
    }

    if (definitionIndex === 0) {
      buttonMessage = buttonStartMessage;
    } else if (definitionIndex === definitionsFormatted.length - 1) {
      buttonMessage = buttonEndMessage;
    } else {
      buttonMessage = buttonNextMessage;
    }
  };

  const dispatch = createEventDispatcher();

  onMount(() => {
    mounted = true;

    const QUERY = "(prefers-reduced-motion: no-preference)";
    allowAnimation.set(window.matchMedia(QUERY).matches);

    const ro = new ResizeObserver(([entry]) => {
      dispatch("update");
    });

    ro.observe(containerElement);

    dispatch("update");

    return () => ro.disconnect();
  });
</script>

<style>
  @keyframes blink {
    from,
    to {
      border-color: lime;
    }

    50% {
      border-color: transparent;
    }
  }

  :global(body) {
    font-family: var(--sans-serif-font);
  }

  :global(.active-letter::after) {
    content: "";
    border-right-color: lime;
    border-right-style: solid;
    border-right-width: 5px;
    animation: 1s blink step-end infinite;
  }

  @media (prefers-reduced-motion) {
    :global(.active-letter::after) {
      border-right: none;
      animation: none;
    }
  }

  .container {
    padding-top: var(--s1);
    padding-bottom: var(--s2);
    border-top: 1px solid #000;
    border-bottom: 1px solid #000;
  }

  .button-container {
    padding-left: 2px;
  }

  .invisible {
    opacity: 0;
  }

  .terminal {
    padding: var(--s1);
    overflow-y: scroll;
    color: #fff;
    background-color: #293845;
  }

  .terminal p,
  .terminal pre {
    margin: 0;
  }

  .code {
    font-family: "SFMono-Regular", Courier, monospace;
  }
</style>

<div class="container" bind:this="{containerElement}">
  <Stack stackSpace="var(--s1)">
    <Stack>
      <h2>{@html headline}</h2>

      <p>{@html standfirst}</p>
    </Stack>

    <Stack>
      <h3 class="code">{@html promptHeading}</h3>
      <div class="terminal code">
        <p>
          {#each promptLine as line}
            {line}<br />
          {/each}
        </p>
      </div>
    </Stack>

    {#if mounted}
      <div class="button-container">
        <SimpleButton on:click="{handleClick}" message="{buttonMessage}" />
      </div>
    {/if}

    <Stack>
      <h3 class="code">
        {#if definitionIndex > 0}
          {`${outputHeading}: ${definitionIndex} of ${
            definitionsFormatted.length - 1
          }`}
        {:else}
          {outputHeading}
        {/if}
      </h3>

      <div class="terminal code">
        <pre
          class:visually-hidden="{mounted}"
          aria-live="polite">
{#if mounted}
{@html currentDefinition}
{:else}
{#each definitionsFormatted.filter((_,i) => i !== 0) as poem }
{@html poem}
{`

-------------------

`}
{/each}
{/if}
		</pre>

        {#if mounted}
          {#key letters}
            <p aria-hidden="true" use:typewriter>
              {#each letters as letter}
                {#if letter === "\n"}
                  <br />
                {:else}
                  <span class:invisible="{$allowAnimation}">{@html letter}</span
                  >
                {/if}
              {/each}
            </p>
          {/key}
        {/if}
      </div>
    </Stack>
  </Stack>

  <p class="font-size:small">{@html source}</p>
</div>
